import type {
  RetailhubEventsData,
  Callback,
  MultiEventCallback,
  ListenerOptions,
  RetailhubEventsConstructorOptions,
} from "../types";
import { Logger } from "../utils/logger";
import { createHistory, type HistoryItem, type ListenersMap } from "../utils/history";

export function RetailhubEvents(options?: RetailhubEventsConstructorOptions) {
  const { listenerLimit: listerLimitOptions = 100, initialEvents = [] } =
    options ?? {};

  const listeners: ListenersMap = {};

  // Store for multi-event listeners: tracks collected data per listener
  const multiEventStore: Map<MultiEventCallback, {
    events: string[];
    collected: Record<string, any>;
    hasFired: boolean;
  }> = new Map();

  const historyLimit: number = 30;

  const listenerLimit: number = listerLimitOptions;

  const logger = new Logger(options?.logEnabled || false);

  const history = createHistory(historyLimit, listeners);

  function enableLogging(): void {
    logger.enable();
  }

  function disableLogging(): void {
    logger.disable();
  }

  function on<K extends string>(
    event: K | K[],
    callback: Callback<K> | MultiEventCallback,
    options?: ListenerOptions
  ) {
    // Handle array of events
    if (Array.isArray(event)) {
      const events = event as string[];
      logger.log(`Multi-event listener added for events: ${events.join(', ')}`, { callback });

      // Initialize storage for this multi-event listener
      multiEventStore.set(callback as MultiEventCallback, {
        events: events,
        collected: {},
        hasFired: false,
      });

      // Create internal handler for each event
      events.forEach((evt) => {
        const internalHandler = (data: any) => {
          const store = multiEventStore.get(callback as MultiEventCallback);
          if (!store) return;

          // Update collected data
          store.collected[evt] = data;

          // Check if all events have been received
          const allEventsReceived = store.events.every((e) => e in store.collected);

          if (allEventsReceived) {
            (callback as MultiEventCallback)({ ...store.collected });
          }
        };

        // Register the internal handler
        if (listeners[evt]) {
          listeners[evt].push(internalHandler);
        } else {
          listeners[evt] = [internalHandler];
        }
      });

      // Handle useHistory for multi-events
      if (options?.useHistory && history.length > 0) {
        const store = multiEventStore.get(callback as MultiEventCallback);
        if (store) {
          events.forEach((evt) => {
            const historyEvent = history.find((h) => h.event === evt);
            if (historyEvent) {
              store.collected[evt] = historyEvent.data;
            }
          });

          // Check if all events are in history
          const allEventsInHistory = events.every((e) => e in store.collected);
          if (allEventsInHistory) {
            (callback as MultiEventCallback)({ ...store.collected });
          }
        }
      }

      return;
    }

    // Handle single event (original logic)
    const singleEvent = event as string;
    if (listeners[singleEvent] && listeners[singleEvent].length >= listenerLimit) {
      console.warn(
        `Warning: Listener limit of ${listenerLimit} reached for event '${String(singleEvent)}'.`
      );
    }
    if (listeners[singleEvent]) {
      listeners[singleEvent].push(callback);
    } else {
      listeners[singleEvent] = [callback];
    }
    logger.log(`New listener added for event '${String(singleEvent)}'.`, { callback });
    if (options?.useHistory) {
      if (history.length > 0) {
        history
          .filter((historyEvent) => historyEvent.event === singleEvent)
          .forEach((historyEvent) => (callback as Callback)(historyEvent.data));
      }
    }
  }

  function off<K extends string>(
    event: K,
    callback?: Callback<K>
  ) {
    if (!callback) {
      delete listeners[event as string];
      logger.log(`Event listener deleted: '${String(event)}'`);
    } else if (listeners[event as string]) {
      listeners[event as string] = listeners[event as string].filter(
        (listener) => listener !== callback
      );
      logger.log(`Event listener deleted: '${String(event)}'`);
    }
  }

  function once<K extends string>(
    event: K | K[],
    callback: Callback<K> | MultiEventCallback,
    options?: ListenerOptions
  ) {
    // Handle array of events
    if (Array.isArray(event)) {
      const events = event as string[];
      logger.log(`Multi-event 'once' listener added for events: ${events.join(', ')}`, { callback });

      // Initialize storage for this multi-event once listener
      const store = {
        events: events,
        collected: {} as Record<string, any>,
        hasFired: false,
      };

      // Create internal handlers
      const internalHandlers: Record<string, (data: any) => void> = {};

      events.forEach((evt) => {
        const internalHandler = (data: any) => {
          if (store.hasFired) return;

          // Update collected data
          store.collected[evt] = data;

          // Check if all events have been received
          const allEventsReceived = store.events.every((e) => e in store.collected);

          if (allEventsReceived && !store.hasFired) {
            store.hasFired = true;
            (callback as MultiEventCallback)({ ...store.collected });

            // Clean up all internal handlers
            events.forEach((e) => {
              const handler = internalHandlers[e];
              if (handler && listeners[e]) {
                listeners[e] = listeners[e].filter((h) => h !== handler);
              }
            });
          }
        };

        internalHandlers[evt] = internalHandler;

        // Register the internal handler
        if (listeners[evt]) {
          listeners[evt].push(internalHandler);
        } else {
          listeners[evt] = [internalHandler];
        }
      });

      // Handle useHistory for multi-events once
      if (options?.useHistory && history.length > 0) {
        events.forEach((evt) => {
          const historyEvent = history.find((h) => h.event === evt);
          if (historyEvent) {
            store.collected[evt] = historyEvent.data;
          }
        });

        // Check if all events are in history
        const allEventsInHistory = events.every((e) => e in store.collected);
        if (allEventsInHistory && !store.hasFired) {
          store.hasFired = true;
          (callback as MultiEventCallback)({ ...store.collected });

          // Clean up handlers
          events.forEach((e) => {
            const handler = internalHandlers[e];
            if (handler && listeners[e]) {
              listeners[e] = listeners[e].filter((h) => h !== handler);
            }
          });
        }
      }

      return;
    }

    // Handle single event (original logic)
    const singleEvent = event as string;
    const onceCallback: Callback<K> = (data) => {
      (callback as Callback)(data);
      off(singleEvent, onceCallback);
    };
    on(singleEvent, onceCallback, options);
  }

  function emit<K extends string>(
    event: K,
    data: any
  ) {
    logger.log(`Event '${String(event)}' emitted.`, { data });
    history.push({ event: String(event), data });
  }

  initialEvents.forEach((event) => emit(event.event, event.data));

  return {
    enableLogging,
    disableLogging,
    on,
    off,
    once,
    emit,
  };
}
